import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { skillsApi, categoriesApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import StarButton from '../components/StarButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Search, Eye, Folder, TrendingUp, Sparkles, Clock,
  PenTool, Briefcase, Megaphone, GraduationCap, Palette, Workflow,
  Bot, Code, BarChart3, FileText, BookOpen, Globe, 
  Shield, Server, Microscope, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BrowsePage = () => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    loadData();
  }, [sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, skillsRes] = await Promise.all([
        categoriesApi.list().catch(() => ({ data: [] })),
        skillsApi.list({ sort: sortBy, limit: 50 }).catch(() => ({ data: [] })),
      ]);
      setCategories(categoriesRes.data);
      setSkills(skillsRes.data);
    } catch (error) {
      console.error('Failed to load browse data', error);
      toast.error('Failed to load browse data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // TODO: Implement search
  };

  const getCategorySkillCount = (categorySlug) => {
    return skills.filter(s => s.category_id === categorySlug).length;
  };

  const getCategoryIcon = (slug) => {
    const iconMap = {
      'writing-content': PenTool,
      'business-productivity': Briefcase,
      'marketing-sales': Megaphone,
      'education-learning': GraduationCap,
      'creative-design': Palette,
      'skills-workflows': Workflow,
      'agents-ai': Bot,
      'developer-tools': Code,
      'data-analytics': BarChart3,
      'documents-files': FileText,
      'research-knowledge': BookOpen,
      'web-apps': Globe,
      'security-compliance': Shield,
      'devops-infrastructure': Server,
      'science-research': Microscope,
      'custom-experimental': Zap,
    };
    return iconMap[slug] || Folder;
  };

  const CategoryCard = ({ category }) => {
    const skillCount = getCategorySkillCount(category.slug);
    const IconComponent = getCategoryIcon(category.slug);
    return (
      <Link to={`/categories/${category.slug}`}>
        <GlassCard hover className="p-6 h-full">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-xl flex-shrink-0">
              <IconComponent className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-manrope font-bold text-text-dark dark:text-dark-text mb-1">
                {category.name}
              </h3>
              <p className="text-sm text-text-gray dark:text-dark-text-secondary line-clamp-2 mb-3">
                {category.description}
              </p>
              <div className="flex items-center justify-between">
                <Badge className="bg-primary/10 text-primary border-0">
                  {skillCount} {skillCount === 1 ? 'skill' : 'skills'}
                </Badge>
                <span className="text-xs text-text-gray dark:text-dark-text-secondary">Browse →</span>
              </div>
            </div>
          </div>
        </GlassCard>
      </Link>
    );
  };

  return (
    <div className="space-y-8" data-testid="browse-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2" data-testid="browse-title">
          Discover Skills
        </h1>
        <p className="text-lg text-text-gray dark:text-dark-text-secondary">
          Explore Claude Skills organized by category
        </p>
      </div>

      {/* Search & Filters */}
      <GlassCard variant="strong" className="p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-text-gray dark:text-dark-text-secondary" />
              <Input
                type="text"
                placeholder="Search skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 glass border-0"
                data-testid="search-input"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <Button
              variant={sortBy === 'recent' ? 'default' : 'outline'}
              onClick={() => setSortBy('recent')}
              className={`rounded-xl ${sortBy === 'recent' ? 'gradient-primary text-white' : 'glass'}`}
              data-testid="sort-recent-button"
            >
              <Clock className="mr-2 h-4 w-4" />
              Recent
            </Button>
            <Button
              variant={sortBy === 'trending' ? 'default' : 'outline'}
              onClick={() => setSortBy('trending')}
              className={`rounded-xl ${sortBy === 'trending' ? 'gradient-primary text-white' : 'glass'}`}
              data-testid="sort-trending-button"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Trending
            </Button>
            <Button
              variant={sortBy === 'featured' ? 'default' : 'outline'}
              onClick={() => setSortBy('featured')}
              className={`rounded-xl ${sortBy === 'featured' ? 'gradient-primary text-white' : 'glass'}`}
              data-testid="sort-featured-button"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Featured
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Category Cards Grid */}
      <div>
        <h2 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text mb-6">
          Browse by Category
        </h2>
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <GlassCard key={i} className="h-40 skeleton" data-testid={`skeleton-${i}`}></GlassCard>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <GlassCard variant="strong" className="text-center py-16" data-testid="empty-state">
            <Folder className="h-16 w-16 mx-auto mb-4 text-text-light dark:text-dark-text-secondary opacity-50" />
            <h3 className="text-xl font-semibold text-text-dark dark:text-dark-text mb-2">No categories found</h3>
            <p className="text-text-gray dark:text-dark-text-secondary">Categories will appear here</p>
          </GlassCard>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="categories-grid">
            {categories.map((category) => (
              <CategoryCard key={category.slug} category={category} />
            ))}
          </div>
        )}
      </div>

      {/* Recent Skills Preview */}
      {skills.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text">
              {sortBy === 'trending' ? 'Trending Skills' : sortBy === 'featured' ? 'Featured Skills' : 'Recent Skills'}
            </h2>
            <Button
              variant="outline"
              onClick={() => navigate('/browse-all')}
              className="rounded-xl glass"
            >
              View All →
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.slice(0, 8).map((skill) => (
              <Link key={skill.id} to={`/skills/${skill.id}`}>
                <GlassCard hover className="p-5 h-full">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-base font-manrope font-bold text-text-dark dark:text-dark-text pr-2 line-clamp-1">
                      {skill.name}
                    </h3>
                    <StarButton 
                      skillId={skill.id} 
                      starCount={skill.star_count || 0}
                      size="small"
                      showCount={false}
                    />
                  </div>
                  
                  <p className="text-xs text-text-gray dark:text-dark-text-secondary line-clamp-2 mb-3">
                    {skill.description || 'No description'}
                  </p>
                  
                  {/* Creator */}
                  <div className="flex items-center gap-2 mb-3">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={skill.creator?.avatar_url} />
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {skill.creator?.username?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-text-gray dark:text-dark-text-secondary truncate">
                      {skill.creator?.username || 'Unknown'}
                    </span>
                  </div>
                  
                  {/* Category Badge */}
                  {skill.category && (
                    <Badge className="bg-primary/10 text-primary border-0 text-xs mb-3">
                      {typeof skill.category === 'object' ? skill.category.name : skill.category}
                    </Badge>
                  )}
                  
                  {/* Stats */}
                  <div className="flex items-center gap-3 text-xs text-text-gray dark:text-dark-text-secondary">
                    <div className="flex items-center gap-1">
                      <span>{skill.star_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>{skill.view_count || 0}</span>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowsePage;